import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Star, Award, Calendar, ShieldCheck, MapPin, Users, Activity } from 'lucide-react';
// Mock data for the profile
const profileData = {
  id: 1,
  name: 'Sarah Jenkins',
  role: 'Medical Lead',
  location: 'San Francisco, CA',
  reputationScore: 98,
  reliability: '100%',
  eventsCompleted: 24,
  connections: 156,
  about: 'Experienced medical professional specializing in sports event triage and emergency response. Passionate about runner safety and team coordination.',
  skills: ['Emergency Response', 'Team Leadership', 'CPR/AED Certified', 'Crowd Management'],
  certifications: [{
    name: 'Advanced First Aid',
    issuer: 'Red Cross',
    expiry: '2026-12-01',
    status: 'Active'
  }, {
    name: 'Medical License (RN)',
    issuer: 'State Board',
    expiry: '2027-01-10',
    status: 'Active'
  }],
  badges: [{
    name: 'Top 1% Rated',
    icon: Star,
    color: 'text-yellow-400',
    bg: 'bg-yellow-400/10'
  }, {
    name: '5+ Years Veteran',
    icon: Award,
    color: 'text-purple-400',
    bg: 'bg-purple-400/10'
  }, {
    name: 'Perfect Attendance',
    icon: Activity,
    color: 'text-[var(--success)]',
    bg: 'bg-[var(--success)]/10'
  }],
  eventHistory: [{
    name: 'City Marathon 2025',
    role: 'Medical Tent Captain',
    rating: 5.0,
    date: 'Oct 2025'
  }, {
    name: 'Bay Area Half 2025',
    role: 'Medical Staff',
    rating: 4.9,
    date: 'May 2025'
  }, {
    name: 'Winter 10K Classic',
    role: 'First Aid Responder',
    rating: 5.0,
    date: 'Dec 2024'
  }]
};
export function VolunteerProfile() {
  const {
    id
  } = useParams();
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} className="space-y-6 max-w-5xl mx-auto" data-id="element-633">
      {/* Back Navigation */}
      <Link to="/dashboard/volunteers" className="inline-flex items-center gap-2 text-[var(--muted)] hover:text-white transition-colors text-sm font-medium" data-id="element-634">
        <ArrowLeft className="w-4 h-4" data-id="element-635" />
        Back to Volunteers
      </Link>

      {/* Header Profile Card */}
      <div className="bg-[var(--surface)] border border-white/5 rounded-xl p-6 md:p-8 relative overflow-hidden" data-id="element-636">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[var(--accent)]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" data-id="element-637" />

        <div className="relative flex flex-col md:flex-row gap-8 items-start md:items-center" data-id="element-638">
          <div className="w-24 h-24 rounded-full bg-gradient-to-tr from-[var(--accent)] to-emerald-400 p-1 shrink-0" data-id="element-639">
            <div className="w-full h-full bg-[var(--surface)] rounded-full flex items-center justify-center text-3xl font-display font-bold text-white" data-id="element-640">
              {profileData.name.charAt(0)}
            </div>
          </div>

          <div className="flex-1" data-id="element-641">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2" data-id="element-642">
              <div data-id="element-643">
                <h1 className="text-3xl font-display font-bold text-white flex items-center gap-2" data-id="element-644">
                  {profileData.name}
                  <ShieldCheck className="w-6 h-6 text-[var(--accent)]" data-id="element-645" />
                </h1>
                <p className="text-lg text-[var(--muted)]" data-id="element-646">
                  {profileData.role}
                </p>
              </div>
              <div className="flex items-center gap-4" data-id="element-647">
                <button className="bg-white/5 hover:bg-white/10 border border-white/10 text-white px-4 py-2 rounded-md font-medium text-sm transition-colors" data-id="element-648">
                  Message
                </button>
                <button className="bg-[var(--accent)] hover:bg-[#b3e622] text-black px-4 py-2 rounded-md font-semibold text-sm transition-colors" data-id="element-649">
                  Assign Role
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-sm text-[var(--muted)] mt-4" data-id="element-650">
              <span className="flex items-center gap-1.5" data-id="element-651">
                <MapPin className="w-4 h-4" data-id="element-652" /> {profileData.location}
              </span>
              <span className="flex items-center gap-1.5" data-id="element-653">
                <Calendar className="w-4 h-4" data-id="element-654" /> {profileData.eventsCompleted}{' '}
                Events
              </span>
              <span className="flex items-center gap-1.5" data-id="element-655">
                <Users className="w-4 h-4" data-id="element-656" /> {profileData.connections}{' '}
                Connections
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-657">
        {/* Left Column: Stats & Badges */}
        <div className="space-y-6" data-id="element-658">
          {/* Reputation Score */}
          <div className="bg-[var(--surface)] border border-white/5 rounded-xl p-6" data-id="element-659">
            <h3 className="text-sm font-medium text-[var(--muted)] uppercase tracking-wider mb-4" data-id="element-660">
              Reputation Score
            </h3>
            <div className="flex items-end gap-3 mb-2" data-id="element-661">
              <span className="text-5xl font-display font-bold text-[var(--accent)]" data-id="element-662">
                {profileData.reputationScore}
              </span>
              <span className="text-[var(--muted)] mb-1" data-id="element-663">/ 100</span>
            </div>
            <div className="w-full bg-white/5 rounded-full h-2 mt-4 overflow-hidden" data-id="element-664">
              <div className="h-full bg-[var(--accent)] rounded-full" style={{
              width: `${profileData.reputationScore}%`
            }} data-id="element-665" />
            </div>
            <div className="mt-4 flex justify-between text-sm" data-id="element-666">
              <span className="text-[var(--muted)]" data-id="element-667">Reliability</span>
              <span className="text-white font-medium" data-id="element-668">
                {profileData.reliability}
              </span>
            </div>
          </div>

          {/* Badges */}
          <div className="bg-[var(--surface)] border border-white/5 rounded-xl p-6" data-id="element-669">
            <h3 className="text-sm font-medium text-[var(--muted)] uppercase tracking-wider mb-4" data-id="element-670">
              Achievements
            </h3>
            <div className="space-y-3" data-id="element-671">
              {profileData.badges.map((badge, i) => <div key={i} className="flex items-center gap-3 bg-white/[0.02] border border-white/5 rounded-lg p-3" data-id="element-672">
                  <div className={`p-2 rounded-md ${badge.bg}`} data-id="element-673">
                    <badge.icon className={`w-4 h-4 ${badge.color}`} data-id="element-674" />
                  </div>
                  <span className="text-sm font-medium text-white" data-id="element-675">
                    {badge.name}
                  </span>
                </div>)}
            </div>
          </div>
        </div>

        {/* Right Column: Details */}
        <div className="lg:col-span-2 space-y-6" data-id="element-676">
          {/* About & Skills */}
          <div className="bg-[var(--surface)] border border-white/5 rounded-xl p-6" data-id="element-677">
            <h3 className="text-lg font-medium text-white mb-3" data-id="element-678">About</h3>
            <p className="text-[var(--muted)] text-sm leading-relaxed mb-6" data-id="element-679">
              {profileData.about}
            </p>

            <h3 className="text-sm font-medium text-[var(--muted)] uppercase tracking-wider mb-3" data-id="element-680">
              Top Skills
            </h3>
            <div className="flex flex-wrap gap-2" data-id="element-681">
              {profileData.skills.map((skill, i) => <span key={i} className="bg-white/5 border border-white/10 text-white px-3 py-1.5 rounded-md text-sm" data-id="element-682">
                  {skill}
                </span>)}
            </div>
          </div>

          {/* Event History */}
          <div className="bg-[var(--surface)] border border-white/5 rounded-xl p-6" data-id="element-683">
            <h3 className="text-lg font-medium text-white mb-4" data-id="element-684">
              Event History
            </h3>
            <div className="space-y-4" data-id="element-685">
              {profileData.eventHistory.map((event, i) => <div key={i} className="flex items-center justify-between border-b border-white/5 last:border-0 pb-4 last:pb-0" data-id="element-686">
                  <div data-id="element-687">
                    <h4 className="text-white font-medium" data-id="element-688">{event.name}</h4>
                    <p className="text-sm text-[var(--muted)]" data-id="element-689">
                      {event.role} • {event.date}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 bg-white/5 px-2 py-1 rounded-md" data-id="element-690">
                    <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" data-id="element-691" />
                    <span className="text-sm font-medium text-white" data-id="element-692">
                      {event.rating.toFixed(1)}
                    </span>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </motion.div>;
}